/** @prettier */
import { driver } from 'driver.js';
import { LOCATION_CHANGE, push } from 'react-router-redux';
import { delay } from 'redux-saga';

import { track } from '@xanadu/ui-shared/interfaces/woopra';
import { APP_LOADED, changeProduct } from '@xanadu/ui-shared/modules/application/actions';
import { batchEventUpdate } from '@xanadu/ui-shared/modules/event/actions';
import * as eventsApi from '@xanadu/ui-shared/modules/event/api';
import { batchEventParticipantUpdate } from '@xanadu/ui-shared/modules/eventParticipants/actions';
import { marketUpdate } from '@xanadu/ui-shared/modules/market/actions';
import { updateAllOffersByServer } from '@xanadu/ui-shared/modules/openOffers/actions';
import * as openOffersApi from '@xanadu/ui-shared/modules/openOffers/api';
import { setPrices } from '@xanadu/ui-shared/modules/price/actions';
import { batchRunnerUpdate } from '@xanadu/ui-shared/modules/runner/actions';
import {
  addUnsubmittedOffers,
  cleanUnsubmittedOffers,
} from '@xanadu/ui-shared/modules/unsubmittedOffers/actions';
import { registrationRequest } from '@xanadu/ui-shared/modules/userRegistration/actions';

import { PRODUCTS } from '../../constants';
import { DRIVER_STEPS, RESTRICTED_PAGES } from '../../constants/customerOnboarding';

import { dangerouslyGetStore } from '../../configureStore';
import { triggerUpdateRightSidebar } from '../rightSidebar/actions';
import { all, call, fork, put, race, select, take } from 'redux-saga/effects';

import { START_TOUR_MODE, setTourMode, toggleCustomerOnboardingPopup } from './actions';
import * as api from './api';

function* showCustomerOnboarding() {
  let eligiblePage = RESTRICTED_PAGES.every(
    (path) => window.location.pathname.indexOf(path) === -1
  );

  if (eligiblePage) {
    yield put(
      toggleCustomerOnboardingPopup({
        visible: true,
        firstTimeVisitor: true,
      })
    );
  } else {
    while (!eligiblePage) {
      const { locationChange, tourStarted } = yield race({
        locationChange: take(LOCATION_CHANGE),
        tourStarted: take(START_TOUR_MODE),
      });

      if (tourStarted) {
        eligiblePage = true;
      }

      if (
        locationChange &&
        RESTRICTED_PAGES.every((path) => window.location.pathname.indexOf(path) === -1)
      ) {
        yield delay(600);
        yield put(
          toggleCustomerOnboardingPopup({
            visible: true,
            firstTimeVisitor: true,
          })
        );

        eligiblePage = true;
      }
    }
  }
}

function* handleCustomerOnboarding() {
  yield take(APP_LOADED);

  yield delay(2000);

  const onboardingEnabled = yield select((state) =>
    state.getIn(['application', 'onboardingEnabled'])
  );

  if (onboardingEnabled) {
    try {
      const onboardingAlreadyVisited = localStorage.getItem('mb-onboarding-visited');
      const userReturningTimes = localStorage.getItem('mb-user-returning-times');
      const showOnboarding = parseInt(userReturningTimes, 10) < 4;

      let isOneTrustCookiePopupClosed =
        !window.getCkyConsent ||
        (window.cookieyes &&
          typeof window.getCkyConsent === 'function' &&
          window.getCkyConsent().isUserActionCompleted);

      if (showOnboarding && (!onboardingAlreadyVisited || onboardingAlreadyVisited !== 'true')) {
        if (isOneTrustCookiePopupClosed) {
          yield call(showCustomerOnboarding);
        } else {
          const channel = yield call(api.createOneTrustChannel);

          while (!isOneTrustCookiePopupClosed) {
            yield take(channel);
            yield delay(400);
            yield call(showCustomerOnboarding);

            isOneTrustCookiePopupClosed = true;
          }
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Error accessing localStorage', e.message);
    }
  }
}

/**
 * This saga is used to add fake event data related unsubmitted offers into the Redux store.
 * Still not 100% convinced where to put this saga
 */
function* handleFakeEventDataStorage({ eventParticipants, runners, markets, events }) {
  if (runners) {
    const formattedRunners = Object.keys(runners).map((key) => runners[key]);
    yield put(batchRunnerUpdate(formattedRunners));
    yield fork(put, setPrices(formattedRunners));
  }
  if (markets) yield put(marketUpdate(Object.keys(markets).map((key) => markets[key])));

  if (eventParticipants) {
    yield put(
      batchEventParticipantUpdate(
        Object.keys(eventParticipants).map((key) => eventParticipants[key])
      )
    );
  }

  if (events) {
    const eventIds = Object.keys(events);

    yield put(batchEventUpdate(eventIds.map((key) => events[key])));

    return eventIds;
  }

  return [];
}

function* handleTourMode() {
  const isLoggedIn = yield select((state) => state.getIn(['user', 'isLoggedIn']));

  const store = dangerouslyGetStore();

  const wizard = driver();

  const onDestroyed = () => {
    store.dispatch(cleanUnsubmittedOffers());
    store.dispatch(
      setTourMode({
        tourMode: false,
      })
    );

    track('Marketing', 'Tour closed');
  };

  const onNextClick = (_, __, config) => {
    const { state: driverState } = config;

    track('Marketing', `Tour Step ${driverState.activeIndex + 1} Viewed`);

    if (driverState.activeIndex === 3) {
      store.dispatch(
        triggerUpdateRightSidebar({
          content: 'betslip',
          visible: true,
          metadata: {
            betslipTab: 'active-bets',
          },
        })
      );
    }

    if (driverState.activeIndex === 4) {
      if (!isLoggedIn) {
        store.dispatch(registrationRequest());

        track('Marketing', 'Reg opened from Tour');
      }
    }

    wizard.moveNext();
  };

  const onPrevClick = (_, __, config) => {
    const { state: driverState } = config;

    if (driverState.activeIndex === 4) {
      store.dispatch(
        triggerUpdateRightSidebar({
          content: 'betslip',
          visible: true,
          metadata: {
            betslipTab: 'betslip',
          },
        })
      );
    }

    wizard.movePrevious();
  };

  try {
    wizard.setConfig({
      popoverClass: 'driverjs-theme',
      doneBtnText: !isLoggedIn ? 'Join Now' : 'End Tour',
      prevBtnText: 'Previous',
      steps: DRIVER_STEPS,
      allowClose: false,
      showProgress: false,
      popoverOffset: 8,
      stagePadding: 8,
      stageRadius: 4,
      disableActiveInteraction: true,
      onDestroyed,
      onNextClick,
      onPrevClick,
    });

    yield call(wizard.drive);

    const onClosingOnboarding = (event) => {
      const isOnboardingCloseButton = event.target.classList.contains('onboarding-close-btn');

      if (isOnboardingCloseButton) {
        wizard.destroy();

        document.removeEventListener('click', onClosingOnboarding);
      }
    };

    // resize throttle
    let interval = null;

    const onWindowResize = () => {
      clearInterval(interval);

      interval = setInterval(() => {
        wizard.refresh();
        clearInterval(interval);
      }, 400);

      if (window.innerWidth < 1200) {
        wizard.destroy();

        window.removeEventListener('resize', onWindowResize);
      }
    };

    document.addEventListener('click', onClosingOnboarding);
    window.addEventListener('resize', onWindowResize);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Error handling the tour', error.message);
  }
}

function* watchForTourMode() {
  while (true) {
    yield take(START_TOUR_MODE);

    const rightSidebarOpen = yield select((state) => state.getIn(['rightSidebar', 'visible']));

    const { events, markets, runners, eventParticipants } = yield call(eventsApi.loadFakeEvent);

    const offers = yield call(openOffersApi.loadFakeOpenOffer);

    yield call(handleFakeEventDataStorage, {
      eventParticipants,
      runners,
      markets,
      events,
    });

    yield put(updateAllOffersByServer(offers, { fakeData: true }));

    yield put(
      triggerUpdateRightSidebar({
        visible: true,
        content: 'betslip',
        metadata: {
          betslipTab: 'betslip',
        },
      })
    );

    yield put(
      addUnsubmittedOffers([
        {
          cid: 'ob-wizard-offer-lay',
          side: 'lay',
          runnerId: 'ob-wizard-runner-id-1',
          eventId: 'ob-wizard-event-id',
          backersStake: 20,
        },
      ])
    );

    yield put(
      addUnsubmittedOffers([
        {
          cid: 'ob-wizard-offer-back',
          side: 'back',
          runnerId: 'ob-wizard-runner-id-1',
          eventId: 'ob-wizard-event-id',
          backersStake: 20,
        },
      ])
    );

    if (!rightSidebarOpen) {
      yield delay(200);
    }

    if (window.location.pathname !== '/') {
      yield put(changeProduct(PRODUCTS.exchange));
      yield put(push('/'));

      yield delay(1000);
    }

    yield put(setTourMode({ tourMode: true }));

    yield fork(handleTourMode);
  }
}

export default function* root() {
  yield all([fork(handleCustomerOnboarding), fork(watchForTourMode)]);
}
